import { useEffect, useState } from 'react';
import { api, ApiError } from '../../shared/api/client';
import { Button } from '../../shared/ui/components';
import { useMe } from '../session/SessionProvider';

type Now = { service: { id: string; title: string } | null; attended: boolean; hasLocation: boolean };
const CONSENT_KEY = 'keora.autoAttendance';
const read = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* sin almacenamiento: se vuelve a preguntar */
  }
};
const asked = new Set<string>();

/**
 * Asistencia automática: si estás en KEORA durante un culto y cerca del templo, se registra tu asistencia.
 * Pide permiso una vez; tu ubicación solo se compara con la del templo y no se guarda.
 */
export function AutoAttendance() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const [now, setNow] = useState<Now | null>(null);
  const [state, setState] = useState<'idle' | 'ask' | 'busy' | 'done' | 'far' | 'denied'>('idle');
  const enabled = Boolean(churchId) && me.canOwn('attendance.record');

  useEffect(() => {
    if (!enabled || !churchId) return;
    let alive = true;
    api
      .get<Now>(`/attendance/churches/${churchId}/now`)
      .then((n) => {
        if (!alive || !n.service || n.attended || !n.hasLocation || asked.has(n.service.id)) return;
        setNow(n);
        const consent = read(CONSENT_KEY);
        if (consent === 'yes') void locate(churchId, n.service.id);
        else if (consent !== 'no') setState('ask');
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [churchId, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  function locate(church: string, serviceId: string) {
    asked.add(serviceId);
    if (!('geolocation' in navigator)) return setState('denied');
    setState('busy');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        api
          .post(`/attendance/churches/${church}/services/${serviceId}/check-in-nearby`, { lat: pos.coords.latitude, lng: pos.coords.longitude })
          .then(() => setState('done'))
          .catch((e) => setState(e instanceof ApiError && e.status === 409 ? 'far' : 'idle'));
      },
      () => setState('denied'),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 120000 },
    );
  }

  if (!now?.service || state === 'idle' || state === 'busy' || state === 'far') return null;
  const title = now.service.title;
  return (
    <div className="auto-attendance" role="status">
      {state === 'ask' ? (
        <>
          <span>
            Es hora de <strong>{title}</strong>. ¿Registramos tu asistencia con tu ubicación? Solo se compara con la del templo; no se guarda.
          </span>
          <span className="actions">
            <Button
              onClick={() => {
                write(CONSENT_KEY, 'yes');
                locate(churchId!, now.service!.id);
              }}
            >
              Sí, siempre
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                asked.add(now.service!.id);
                setState('idle');
              }}
            >
              Ahora no
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                write(CONSENT_KEY, 'no');
                setState('idle');
              }}
            >
              Nunca
            </Button>
          </span>
        </>
      ) : state === 'done' ? (
        <span>✓ Registramos tu asistencia a <strong>{title}</strong>. ¡Bienvenido!</span>
      ) : (
        <span className="small">No pudimos usar tu ubicación. Puedes marcar tu asistencia en Agenda → Cultos con «Estoy aquí».</span>
      )}
      {state !== 'ask' ? (
        <Button variant="ghost" aria-label="Cerrar aviso" onClick={() => setState('idle')}>
          ×
        </Button>
      ) : null}
    </div>
  );
}
